import usePageLayout from '../../../hooks/usePageLayout';
import React, { useRef } from "react";
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from "react-helmet";
import ButtonPanelReaction from '../../../components/Buttons/ButtonPanelReaction';
import StoryTitle from '../../../components/StoryTitle';
import { StatisticChart, StatisticParticipants } from '../../../components/PostStatistic';
import ContentState from '../../../components/Brand/ContentState';
import BrandLoader from '../../../components/Brand/BrandLoader';
import useStoryDetails from '../../../hooks/useStoryDetails';
import useInAppView from '../../../inApp/useInAppView';
import StoryDetailFilters from '../../../components/StoryDetailFilters';
import InlineNotice from '../../../components/Brand/InlineNotice';
import useStoryFilters from '../../../hooks/useStoryFilters';
import useStoryNavigation from '../../../hooks/useStoryNavigation';
import { useTranslation } from 'react-i18next';
import { Layout } from 'antd';
import './StoryStatistic.scss';

const { Header, Content, Footer } = Layout;
export default function StoryStatistic() {
	const params = useParams();
	const location = useLocation();
	const storyId = parseInt(params.storyId);
	const filters = useStoryFilters(storyId, 'statistics');
	const { list: scope, story, status, retained, noticeKey, retry } = useStoryDetails(storyId, 'statistics', {
		initialStory: location.state?.storyPreview, area: filters.area, viewerKey: filters.viewerKey,
	});
	const { sectionLink } = useStoryNavigation({ storyId, title: story.title,
		activeSection: 'statistics', detailArea: filters.area });

	const ref_header = useRef(null);
	const ref_footer = useRef(null);
	const { contentHeight, contentTop } = usePageLayout(ref_header, ref_footer);
	const { t } = useTranslation();

	const redPct = scope.filter(s => s.scope <= 3).reduce((sum, s) => sum + s.proc, 0);
	const grayPct = scope.find(s => s.scope === 4)?.proc ?? 0;
	const greenPct = scope.filter(s => s.scope >= 5).reduce((sum, s) => sum + s.proc, 0);
	const hasVotes = scope.some(({ proc, cnt }) => proc > 0 || cnt > 0);
	useInAppView({ kind: 'statistics', storyId, title: story.title, ready: status === 'ready' && !noticeKey && hasVotes,
		resetKey: filters.viewerKey + ':' + filters.area });

	return (
		<Layout className="app-layout story-detail-page">
			<Helmet>
				<title>{story.title}</title>
				<meta name="description" content={story.title} />
			</Helmet>
			<Header ref={ref_header} className="header">
				<h1 className="story-detail-heading">{t('story.statistics')}</h1>
				<StoryDetailFilters mode="statistics" {...filters} onBack={() => filters.onBack(story)} />
				<div className="wrapper-padding">
					<StoryTitle
						activeSection="statistics"
						detailArea={filters.area}
						storyId={storyId}
						title={story.title}>
					</StoryTitle>
				</div>
			</Header>
			<Content
				className="content-layot statistic-content"
				style={{ height: contentHeight, top: contentTop }}
				aria-busy={status === 'loading'}
			>
				{retained && <BrandLoader className="brand-loader--refresh" label={t('brand:loading')} />}
				{noticeKey ? <div className="story-detail-notice"><InlineNotice>{t(`brand:${noticeKey}`)}</InlineNotice></div>
					: status !== 'ready' && !retained ? <ContentState status={status} onRetry={retry} /> : <div className="statistic_wrapper loading-retained-content" inert={retained ? '' : undefined}>
					{hasVotes
						? <StatisticChart scope={scope} className="story-statistic-chart" />
						: <ContentState status="empty" emptyKey={filters.area === 'all' ? 'statisticsEmpty' : 'storyFilteredStatisticsEmpty'} />}
					<StatisticParticipants scope={scope} link={sectionLink('opinions', 'reactions')} />
				</div>}
			</Content>
			<Footer ref={ref_footer}>
				{(status === 'ready' || retained) && hasVotes && !noticeKey && <div className="scope_scale_container wiw-control-band" aria-busy={retained || undefined}>
					<div className="scope_scale">
						<div className="scope_red" style={{ flexBasis: (3 / 7 * 100) + '%' }}>{Math.round(redPct)}%</div>
						<div className="scope_grey" style={{ flexBasis: (1 / 7 * 100) + '%' }}>{Math.round(grayPct)}%</div>
						<div className="scope_green" style={{ flexBasis: (3 / 7 * 100) + '%' }}>{Math.round(greenPct)}%</div>
					</div>
				</div>}
				<ButtonPanelReaction storyId={storyId} />
			</Footer>
		</Layout>
	)
}
